import {UseQueryResult, useQuery} from '@tanstack/react-query';

import api from 'app/modules/Remote/api';
import {
  FeatureStatsApiResponse,
  MFAResponse,
  MultifeatureAnalysisConfig,
} from 'app/src/components/Dashboard/types';
import {getLayer, parseLayerKey} from 'app/utils/layerUtils';

export function useMultiFeatureAnalysisStats(
  organizationId: string,
  {
    featureIds,
    layerKey,
    startDate,
    endDate,
    statAggregation = 'mean',
    timeAggregation = 'monthly',
  }: MultifeatureAnalysisConfig
): UseQueryResult<FeatureStatsApiResponse, Error> {
  // Stable query key including all relevant API parameters
  const queryKey = [
    'aggregatedFeatureStats', // New key base reflects the hook's purpose
    featureIds?.sort().join('-') || 'all-features',
    layerKey,
    startDate,
    endDate,
    statAggregation,
    timeAggregation,
  ];

  return useQuery({
    queryKey,
    queryFn: async () => {
      // The API expects 'area_counts' for all categorical data, but `area_percentages`
      // is used in the frontend for dashboard configs that use the same data but display
      // percentages instead of areas.
      const apiStatAggregation =
        statAggregation === 'area_percentages' ? 'area_counts' : statAggregation;
      const useAllOrgFeatures = !featureIds || featureIds?.length === 0;

      // The API expects a layerId and sourceId, not a layerKey
      const {layerId, sourceId} = parseLayerKey(layerKey);

      const response = await api.dashboardStats(organizationId).getMultiPropertyAnalysis({
        layerKey: layerId,
        sourceId,
        startDate,
        endDate,
        statAggregation: apiStatAggregation,
        timeAggregation,
        featureIds: useAllOrgFeatures ? null : featureIds,
        useAllOrgFeatures,
      });

      const data = response.get('data').toJS() as FeatureStatsApiResponse;

      return filterData(data as MFAResponse, layerKey);
    },
  });
}

function filterData(responseData: MFAResponse, layerKey: string) {
  const layer = getLayer(layerKey);
  const {sourceId} = parseLayerKey(layerKey);

  // We should adjust this to filter for any layer with a data range
  // but we are adjusting planet configs atm and the data ranges for planet
  // are set on the 0-255 pixel color range instead of the actual data range
  // For now, we are clipping only S2 data as it is the default for the dashboard
  if (layer.type !== 'data' || !layer.dataRange || sourceId !== 'S2') {
    return responseData;
  }
  const {
    dataRange: [min, max],
  } = layer;

  // ensure all the data is within the data range, if not, filter out the keys that are outside the range
  const filteredData: MFAResponse = {};
  for (const [featureId, data] of Object.entries(responseData)) {
    if ('error' in data) {
      filteredData[featureId] = {...data};
      continue;
    }
    for (const [date, value] of Object.entries(data.stats)) {
      filteredData[featureId] = {...data};
      if (value && value >= min && value <= max) {
        filteredData[featureId].stats[date] = value;
      } else if (value) {
        if (value > max) {
          filteredData[featureId].stats[date] = max;
        } else if (value < min) {
          filteredData[featureId].stats[date] = min;
        }
      } else {
        filteredData[featureId].stats[date] = null;
      }
    }
  }
  return filteredData;
}
